import { Component, OnInit, AfterViewInit, OnDestroy, ElementRef, ViewChild, NgZone, HostListener } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { forkJoin } from 'rxjs';
import { ExpenseService } from '../../services/expense.service';
import { OrderService } from '../../services/order.service';
import { PermissionService } from '../../services/permission.service';
import { UtilsService } from '../../services/utils.service';
import { ExpenseValidation, ValidationItem, ValidationStatus } from '../../interfaces/expense.interface';
import { ModalAction } from './approve-reject-modal/approve-reject-modal.component';

type TabId = 'combustible' | 'casetas' | 'alimentos' | 'otros' | 'depositos';

/** Catalogo de los 32 estados de la Republica Mexicana. */
const ESTADOS_MEXICO: string[] = [
    'Aguascalientes', 'Baja California', 'Baja California Sur', 'Campeche',
    'Coahuila de Zaragoza', 'Colima', 'Chiapas', 'Chihuahua', 'Ciudad de México',
    'Durango', 'Guanajuato', 'Guerrero', 'Hidalgo', 'Jalisco', 'México',
    'Michoacán de Ocampo', 'Morelos', 'Nayarit', 'Nuevo León', 'Oaxaca', 'Puebla',
    'Querétaro', 'Quintana Roo', 'San Luis Potosí', 'Sinaloa', 'Sonora', 'Tabasco',
    'Tamaulipas', 'Tlaxcala', 'Veracruz de Ignacio de la Llave', 'Yucatán', 'Zacatecas',
];

interface CheckRow { id: number; type: 'food' | 'other' | 'accommodation' | 'fine'; desc: string; monto: number | null; checked: boolean; evidence: string | null; }
interface DepositRow { label: string; monto: number | null; evidence: string | null; }
interface CombustibleRow {
    id: number; km: number; kmRec: number | null; litros: number;
    costo: number | null; quien: string; rendimiento: number | null; evidence: string | null;
}
interface CasetaTagRow { tag_number: string; caseta: string; tramo: string; amount: number; }
interface CasetaEfectivoRow { id: number; caseta: string; tramo: string; amount: number | null; evidence: string | null; }
type CategoriaResumenKey = 'combustible' | 'casetas' | 'alimentos' | 'otros';
interface CategoriaResumen { key: CategoriaResumenKey; label: string; icon: string; total: number; included: boolean; }

@Component({
    selector: 'app-expense-validation',
    templateUrl: './expense-validation.component.html',
    styleUrls: ['./expense-validation.component.scss']
})
export class ExpenseValidationComponent implements OnInit, AfterViewInit, OnDestroy {
    @ViewChild('resumenPanel') resumenPanelRef?: ElementRef<HTMLElement>;
    resumenPanelHeight = 176; // fallback hasta que el ResizeObserver mida el panel real
    private resumenResizeObserver?: ResizeObserver;
    validation: ExpenseValidation | null = null;
    loading = false;
    error = '';

    service: any = null;
    expenseData: any = null;
    estadosMexico = ESTADOS_MEXICO;
    estado: string | null = null;
    savingEstado = false;

    modalVisible = false;
    modalAction: ModalAction = 'approve';
    selectedItem: ValidationItem | null = null;
    serviceCollapsed = false;
    activeTab: TabId = 'combustible';
    resumenExpanded = false;
    resumenInfoOpen = false;
    categoriasResumen: CategoriaResumen[] = [];
    private savedCategoriasIncluidas: Record<string, boolean> | null = null;
    savingCategoriasIncluidas = false;

    initialKm: number | null = null;
    combustibleRows: CombustibleRow[] = [];
    casetasTagPartidaRows: CasetaTagRow[] = [];
    casetasTagRetornoRows: CasetaTagRow[] = [];
    casetasEfectivoPartidaRows: CasetaEfectivoRow[] = [];
    casetasEfectivoRetornoRows: CasetaEfectivoRow[] = [];
    alimentosRows: CheckRow[] = [];
    otrosRows: CheckRow[] = [];
    depositosRows: DepositRow[] = [];
    serviceId: number | null = null;
    tagTollPartidaUploading = false;
    tagTollRetornoUploading = false;
    kmGps: number | null = null;
    kmGoogleMaps: number | null = null;
    savingKmComparison = false;

    tagBoothEntryDate: string | null = null;
    tagBoothEntryTime: string | null = null;
    tagBoothExitDate: string | null = null;
    tagBoothExitTime: string | null = null;
    patioEntryDate: string | null = null;
    patioEntryTime: string | null = null;
    patioExitDate: string | null = null;
    patioExitTime: string | null = null;
    savingTripDates = false;

    isValidated = false;
    validationInfo: { validated_by: string; validated_at: string; validation_is_valid: boolean; validation_inconsistency: string | null } | null = null;

    showValidationModal = false;
    validationIsValid = true;
    validatorName = '';
    inconsistencyDesc = '';
    submittingValidation = false;

    get casetasTagPartidaTotal(): number {
        return this.casetasTagPartidaRows.reduce((s: number, r: CasetaTagRow) => s + (r.amount ?? 0), 0);
    }

    get casetasTagRetornoTotal(): number {
        return this.casetasTagRetornoRows.reduce((s: number, r: CasetaTagRow) => s + (r.amount ?? 0), 0);
    }

    get casetasTagTotal(): number {
        return this.casetasTagPartidaTotal + this.casetasTagRetornoTotal;
    }

    get casetasEfectivoPartidaTotal(): number {
        return this.casetasEfectivoPartidaRows.reduce((s: number, r: CasetaEfectivoRow) => s + (r.amount ?? 0), 0);
    }

    get casetasEfectivoRetornoTotal(): number {
        return this.casetasEfectivoRetornoRows.reduce((s: number, r: CasetaEfectivoRow) => s + (r.amount ?? 0), 0);
    }

    get casetasEfectivoTotal(): number {
        return this.casetasEfectivoPartidaTotal + this.casetasEfectivoRetornoTotal;
    }

    get alimentosTotal(): number {
        return this.alimentosRows
            .filter(r => r.monto)
            .reduce((s, r) => s + r.monto!, 0);
    }

    get otrosTotal(): number {
        return this.otrosRows
            .filter(r => r.monto)
            .reduce((s, r) => s + r.monto!, 0);
    }

    get depositosTotal(): number {
        return this.depositosRows
            .filter(r => r.monto)
            .reduce((s, r) => s + r.monto!, 0);
    }

    get combustibleTotal(): number {
        return this.combustibleRows.reduce((s, r) => s + (r.costo || 0), 0);
    }

    get totalKmRecorridos(): number {
        return this.combustibleRows.reduce((s, r) => s + (r.kmRec || 0), 0);
    }

    get totalLitros(): number {
        return this.combustibleRows.reduce((s, r) => s + r.litros, 0);
    }

    get finalKm(): number | null {
        return this.combustibleRows.length ? this.combustibleRows[this.combustibleRows.length - 1].km : null;
    }

    get kmDiferencia(): number | null {
        if (this.kmGps === null || this.kmGoogleMaps === null) return null;
        return this.kmGps - this.kmGoogleMaps;
    }

    get totalRendimiento(): number | null {
        if (!this.combustibleRows.length || this.initialKm === null) return null;
        const lastKm = this.combustibleRows[this.combustibleRows.length - 1].km;
        const lts = this.totalLitros;
        if (!lts) return null;
        return Math.round(((lastKm - this.initialKm) / lts) * 10000) / 10000;
    }

    get categoriasSeleccionadasCount(): number {
        return this.categoriasResumen.filter(c => c.included).length;
    }

    get totalGastosCalculado(): number {
        return this.categoriasResumen.reduce((s, c) => s + c.total, 0);
    }

    get saldoCalculado(): number {
        return this.depositosTotal - this.totalGastosCalculado;
    }

    get saldoLabel(): string {
        if (this.saldoCalculado > 0) return 'Saldo remanente';
        if (this.saldoCalculado < 0) return 'Saldo adeudado';
        return 'Saldo en equilibrio';
    }

    get saldoSub(): string {
        if (this.saldoCalculado > 0) return 'Total depósitos - Total gastos realizados';
        if (this.saldoCalculado < 0) return 'Total gastos - Total depósitos realizados';
        return 'No existe saldo pendiente.';
    }

    get saldoIcon(): string {
        if (this.saldoCalculado > 0) return 'ti-circle-plus';
        if (this.saldoCalculado < 0) return 'ti-circle-minus';
        return 'ti-info-circle';
    }

    get allAlimentosChecked(): boolean {
        return this.alimentosRows.every(r => r.checked);
    }

    get allOtrosChecked(): boolean {
        return this.otrosRows.every(r => r.checked);
    }

    toggleAllAlimentos(checked: boolean): void {
        this.alimentosRows.forEach(r => r.checked = checked);
    }

    toggleAllOtros(checked: boolean): void {
        this.otrosRows.forEach(r => r.checked = checked);
    }

    canDo(action: string): boolean {
        return this.permissionService.canDo('Control de gastos', action);
    }

    constructor(
        private route: ActivatedRoute,
        private router: Router,
        private expenseService: ExpenseService,
        private orderService: OrderService,
        private spinner: NgxSpinnerService,
        private permissionService: PermissionService,
        private utilsService: UtilsService,
        private ngZone: NgZone
    ) {}

    ngOnInit(): void {
        const id = this.route.snapshot.paramMap.get('id');
        if (id) {
            this.serviceId = parseInt(id);
            this.loadValidation(this.serviceId);
        }
    }

    // El panel "Resumen de cálculo" es fixed-bottom y su altura varía con su
    // contenido (categorías, expandido/colapsado). Se mide en vivo para que el
    // padding-bottom de la página siempre deje visible lo que hay detrás de él.
    ngAfterViewInit(): void {
        if (this.resumenPanelRef?.nativeElement && typeof ResizeObserver !== 'undefined') {
            this.resumenResizeObserver = new ResizeObserver(() => {
                // ResizeObserver corre fuera de NgZone: sin este run(), el binding
                // de padding-bottom en la plantilla nunca se actualiza.
                // Se usa offsetHeight (incluye padding/border) en vez de
                // contentRect.height, que solo mide el área de contenido interna.
                this.ngZone.run(() => {
                    this.resumenPanelHeight = this.resumenPanelRef!.nativeElement.offsetHeight;
                });
            });
            this.resumenResizeObserver.observe(this.resumenPanelRef.nativeElement);
        }
    }

    ngOnDestroy(): void {
        this.resumenResizeObserver?.disconnect();
    }

    loadValidation(serviceId: number): void {
        this.loading = true;
        this.spinner.show();
        forkJoin([
            this.orderService.getId(serviceId),
            this.expenseService.getExpenseByServiceId(serviceId)
        ]).subscribe({
            next: ([service, expense]: [any, any]) => {
                this.service = service;
                this.expenseData = expense;
                this.estado = expense?.estado ?? null;
                this.tagBoothEntryDate = expense?.tag_booth_entry_date ?? null;
                this.tagBoothEntryTime = expense?.tag_booth_entry_time ?? null;
                this.tagBoothExitDate = expense?.tag_booth_exit_date ?? null;
                this.tagBoothExitTime = expense?.tag_booth_exit_time ?? null;
                this.patioEntryDate = expense?.patio_entry_date ?? null;
                this.patioEntryTime = expense?.patio_entry_time ?? null;
                this.patioExitDate = expense?.patio_exit_date ?? null;
                this.patioExitTime = expense?.patio_exit_time ?? null;
                this.savedCategoriasIncluidas = expense?.categorias_incluidas ?? null;
                this.mapRows(service, expense);
                this.loading = false;
                this.spinner.hide();
            },
            error: () => {
                this.error = 'Error al cargar los gastos para validación.';
                this.loading = false;
                this.spinner.hide();
            }
        });
    }

    private mapRows(service: any, expense: any): void {
        const driver = service?.driver?.name;
        const assistant = service?.assistant?.name;
        const driverName = [driver, assistant].filter(Boolean).join(' / ') || '—';

        const deposits = expense?.deposits || service?.deposits || [];
        this.initialKm = deposits.length > 0 ? (parseFloat(deposits[0].mileage) || null) : null;

        // Fusionar gastos individuales + compartidos del grupo
        const grp = expense?.group_expenses || {};
        const merge = (key: string) => [
            ...(expense?.[key] || []),
            ...(grp[key]     || []),
        ];

        const allFuel       = merge('fuel_expenses');
        const allPartida    = merge('toll_partida_expenses');
        const allRetorno    = merge('toll_retorno_expenses');
        const allFood       = merge('food_expenses');
        const allOther      = merge('other_expenses');
        const allAccomm     = merge('accommodation_expenses');
        const allFines      = merge('traffic_fine_expenses');

        this.combustibleRows = allFuel.map((f: any, i: number, arr: any[]) => {
            const km = parseFloat(f.mileage) || 0;
            const prevKm = i > 0 ? (parseFloat(arr[i - 1].mileage) || 0) : this.initialKm;
            const litros = parseFloat(f.liters) || 0;
            const kmRec = prevKm !== null ? km - prevKm : null;
            return {
                id: f.id,
                km,
                kmRec,
                litros,
                costo: parseFloat(f.amount) || null,
                quien: driverName,
                rendimiento: kmRec !== null && litros ? Math.round((kmRec / litros) * 10000) / 10000 : null,
                evidence: f.evidence || null,
            };
        });

        // Fallback for old flat data without trip field
        const allOldTolls: any[] = expense?.toll_expenses || [];
        const effectivePartida = allPartida.length > 0 ? allPartida : allOldTolls.filter((t: any) => !t.trip || t.trip === 'partida');
        const effectiveRetorno = allRetorno.length > 0 ? allRetorno : allOldTolls.filter((t: any) => t.trip === 'retorno');

        const mapTagRow = (t: any): CasetaTagRow => ({
            tag_number: t.tag_number || '—',
            caseta: t.toll_booth?.name || t.caseta || '—',
            tramo: t.carril || t.segment?.name || '—',
            amount: parseFloat(t.amount) || 0,
        });

        const mapEfectivoRow = (t: any): CasetaEfectivoRow => ({
            id: t.id,
            caseta: t.toll_booth?.name || t.caseta || '—',
            tramo: t.segment?.name || t.tramo || '—',
            amount: parseFloat(t.amount) || null,
            evidence: t.evidence || null,
        });

        this.casetasTagPartidaRows = effectivePartida.filter((t: any) => t.source === 'tag').map(mapTagRow);
        this.casetasTagRetornoRows = effectiveRetorno.filter((t: any) => t.source === 'tag').map(mapTagRow);
        this.casetasEfectivoPartidaRows = effectivePartida.filter((t: any) => t.source !== 'tag').map(mapEfectivoRow);
        this.casetasEfectivoRetornoRows = effectiveRetorno.filter((t: any) => t.source !== 'tag').map(mapEfectivoRow);

        this.alimentosRows = allFood.map((f: any) => ({
            id: f.id,
            type: 'food' as const,
            desc: f.food_day || f.description || '—',
            monto: parseFloat(f.amount) || null,
            checked: !!f.is_validated,
            evidence: f.evidence || null,
        }));

        this.otrosRows = [
            ...allOther.map((e: any) => ({
                id: e.id,
                type: 'other' as const,
                desc: e.description || '—',
                monto: parseFloat(e.amount) || null,
                checked: !!e.is_validated,
                evidence: e.evidence || null,
            })),
            ...allAccomm.map((e: any) => ({
                id: e.id,
                type: 'accommodation' as const,
                desc: `Alojamiento${e.stay_duration ? ' (' + e.stay_duration + ')' : ''}`,
                monto: parseFloat(e.amount) || null,
                checked: !!e.is_validated,
                evidence: e.evidence || null,
            })),
            ...allFines.map((e: any) => ({
                id: e.id,
                type: 'fine' as const,
                desc: e.justification || 'Multa de tránsito',
                monto: parseFloat(e.amount) || null,
                checked: false,
                evidence: e.evidence || null,
            }))
        ];

        this.depositosRows = deposits.map((d: any, i: number) => ({
            label: i === 0 ? 'Inicial' : `#${i + 1}`,
            monto: parseFloat(d.amount) || null,
            evidence: d.evidence || null
        }));

        this.kmGps = expense?.km_gps != null ? parseFloat(expense.km_gps) : null;
        // KM Google Maps es siempre la misma distancia (total_distance) enviada en la Carta Porte del servicio.
        this.kmGoogleMaps = expense?.carta_porte_km_google_maps != null ? parseFloat(expense.carta_porte_km_google_maps) : null;

        this.isValidated = !!(expense?.expense_validated);
        if (this.isValidated) {
            this.validationInfo = {
                validated_by:             expense.validated_by ?? '',
                validated_at:             expense.validated_at ?? '',
                validation_is_valid:      expense.validation_is_valid ?? true,
                validation_inconsistency: expense.validation_inconsistency ?? null,
            };
        }

        this.buildCategoriasResumen();
    }

    // Preserva el estado de los toggles (included) entre recargas de datos,
    // usando lo guardado en el backend como base la primera vez.
    private buildCategoriasResumen(): void {
        const prevIncluded = new Map(this.categoriasResumen.map(c => [c.key, c.included]));
        const included = (key: CategoriaResumenKey) =>
            prevIncluded.get(key) ?? this.savedCategoriasIncluidas?.[key] ?? true;

        this.categoriasResumen = [
            { key: 'combustible', label: 'Combustible',  icon: 'ti-gas-station', total: this.combustibleTotal, included: included('combustible') },
            { key: 'casetas',     label: 'Casetas',      icon: 'ti-ticket',      total: this.casetasTagTotal + this.casetasEfectivoTotal, included: included('casetas') },
            { key: 'alimentos',   label: 'Alimentos',    icon: 'ti-tools-kitchen-2', total: this.alimentosTotal, included: included('alimentos') },
            { key: 'otros',       label: 'Otros gastos', icon: 'ti-credit-card', total: this.otrosTotal, included: included('otros') },
        ];
    }

    onKmComparisonBlur(): void {
        if (!this.expenseData?.id) return;
        this.savingKmComparison = true;
        this.expenseService.saveKmComparison(this.expenseData.id, {
            km_gps: this.kmGps,
            km_google_maps: this.kmGoogleMaps,
        }).subscribe({
            next: () => { this.savingKmComparison = false; },
            error: () => { this.savingKmComparison = false; }
        });
    }

    onEstadoChange(): void {
        if (!this.expenseData?.id) return;
        this.savingEstado = true;
        this.expenseService.saveEstado(this.expenseData.id, this.estado).subscribe({
            next: () => { this.savingEstado = false; },
            error: () => { this.savingEstado = false; }
        });
    }

    onTripDateBlur(): void {
        if (!this.expenseData?.id) return;
        this.savingTripDates = true;
        this.expenseService.saveTripDates(this.expenseData.id, {
            tag_booth_entry_date: this.tagBoothEntryDate,
            tag_booth_entry_time: this.tagBoothEntryTime,
            tag_booth_exit_date: this.tagBoothExitDate,
            tag_booth_exit_time: this.tagBoothExitTime,
            patio_entry_date: this.patioEntryDate,
            patio_entry_time: this.patioEntryTime,
            patio_exit_date: this.patioExitDate,
            patio_exit_time: this.patioExitTime,
        }).subscribe({
            next: () => { this.savingTripDates = false; },
            error: () => { this.savingTripDates = false; }
        });
    }

    resumenInfoBottom = 0;
    resumenInfoLeft = 0;
    resumenMethodologyOpen = false;
    resumenMethodologyBottom = 0;
    resumenMethodologyLeft = 0;

    private computePopoverPosition(triggerEl: HTMLElement, popoverWidth: number): { bottom: number; left: number } {
        const rect = triggerEl.getBoundingClientRect();
        const width = Math.min(popoverWidth, window.innerWidth - 48);
        const left = Math.max(16, Math.min(rect.right - width, window.innerWidth - width - 16));
        const bottom = window.innerHeight - rect.top + 8;
        return { bottom, left };
    }

    toggleResumenInfo(event: MouseEvent): void {
        this.resumenInfoOpen = !this.resumenInfoOpen;
        if (this.resumenInfoOpen) {
            const pos = this.computePopoverPosition(event.currentTarget as HTMLElement, 260);
            this.resumenInfoBottom = pos.bottom;
            this.resumenInfoLeft = pos.left;
        }
    }

    toggleResumenMethodology(event: MouseEvent): void {
        this.resumenMethodologyOpen = !this.resumenMethodologyOpen;
        if (this.resumenMethodologyOpen) {
            const pos = this.computePopoverPosition(event.currentTarget as HTMLElement, 620);
            this.resumenMethodologyBottom = pos.bottom;
            this.resumenMethodologyLeft = pos.left;
        }
    }

    @HostListener('document:click', ['$event'])
    onDocumentClickCloseResumenInfo(event: MouseEvent): void {
        const target = event.target as HTMLElement;
        if (this.resumenInfoOpen && !target.closest('.ev-resumen__info-wrap')) {
            this.resumenInfoOpen = false;
        }
        if (this.resumenMethodologyOpen && !target.closest('.ev-resumen__methodology-wrap')) {
            this.resumenMethodologyOpen = false;
        }
    }

    onCategoriaToggle(): void {
        if (!this.expenseData?.id) return;
        this.savingCategoriasIncluidas = true;
        const payload: Record<string, boolean> = {};
        this.categoriasResumen.forEach(c => { payload[c.key] = c.included; });
        this.expenseService.saveCategoriasIncluidas(this.expenseData.id, payload).subscribe({
            next: () => { this.savingCategoriasIncluidas = false; },
            error: () => { this.savingCategoriasIncluidas = false; }
        });
    }

    openModal(item: ValidationItem, action: ModalAction): void {
        this.selectedItem = item;
        this.modalAction = action;
        this.modalVisible = true;
    }

    closeModal(): void {
        this.modalVisible = false;
        this.selectedItem = null;
    }

    onModalConfirmed(result: { action: ModalAction; message: string }): void {
        if (!this.selectedItem) return;
        const id = this.selectedItem.id;
        let request$;

        if (result.action === 'approve') {
            request$ = this.expenseService.approveExpense(id, result.message);
        } else if (result.action === 'reject') {
            request$ = this.expenseService.rejectExpense(id, result.message);
        } else {
            request$ = this.expenseService.requestExpenseInfo(id, result.message);
        }

        const statusMap: Record<ModalAction, ValidationStatus> = {
            approve: 'approved',
            reject: 'rejected',
            info: 'info_requested'
        };

        request$.subscribe({
            next: () => {
                if (this.selectedItem && this.validation) {
                    this.selectedItem.status = statusMap[result.action];
                    this.selectedItem.comments = result.message;
                    this.recalcCounts();
                }
                this.closeModal();
            },
            error: () => {
                alert('Error al procesar la acción.');
                this.closeModal();
            }
        });
    }

    private recalcCounts(): void {
        if (!this.validation) return;
        this.validation.approved_count = this.validation.items.filter(i => i.status === 'approved').length;
        this.validation.rejected_count = this.validation.items.filter(i => i.status === 'rejected').length;
        this.validation.pending_count  = this.validation.items.filter(i => i.status === 'pending').length;
    }

    statusBadge(status: ValidationStatus): { cls: string; label: string } {
        const map: Record<ValidationStatus, { cls: string; label: string }> = {
            pending:        { cls: 'badge bg-warning text-dark', label: 'Pendiente' },
            approved:       { cls: 'badge bg-success',           label: 'Aprobado' },
            rejected:       { cls: 'badge bg-danger',            label: 'Rechazado' },
            info_requested: { cls: 'badge bg-secondary',         label: 'Info solicitada' }
        };
        return map[status] || map.pending;
    }

    formatCurrency(val: number): string {
        return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(val || 0);
    }

    goBack(): void {
        this.router.navigate(['/service/list']);
    }

    triggerTagCsvInput(trip: 'partida' | 'retorno'): void {
        (document.getElementById(`ev-tag-csv-${trip}`) as HTMLInputElement)?.click();
    }

    triggerEvidenceInput(inputId: string): void {
        (document.getElementById(inputId) as HTMLInputElement)?.click();
    }

    onUploadEvidenceCombustible(event: any, row: CombustibleRow): void {
        const file: File = event.target.files[0];
        if (!file) return;

        this.expenseService.uploadItemEvidence('fuel', row.id, file).subscribe({
            next: (res: any) => { row.evidence = res.url; },
        });

        event.target.value = '';
    }

    onUploadEvidenceCashToll(event: any, row: CasetaEfectivoRow): void {
        const file: File = event.target.files[0];
        if (!file || !row.id) return;

        this.expenseService.uploadItemEvidence('toll' as any, row.id, file).subscribe({
            next: (res: any) => { row.evidence = res.url; },
        });

        event.target.value = '';
    }

    onUploadEvidence(event: any, row: CheckRow): void {
        const file: File = event.target.files[0];
        if (!file) return;

        this.expenseService.uploadItemEvidence(row.type, row.id, file).subscribe({
            next: (res: any) => { row.evidence = res.url; },
        });

        event.target.value = '';
    }

    onCheckChange(): void {
        if (!this.expenseData?.id) return;

        const food          = this.alimentosRows.map(r => ({ id: r.id, is_validated: r.checked }));
        const other         = this.otrosRows.filter(r => r.type === 'other').map(r => ({ id: r.id, is_validated: r.checked }));
        const accommodation = this.otrosRows.filter(r => r.type === 'accommodation').map(r => ({ id: r.id, is_validated: r.checked }));

        this.expenseService.validateItems(this.expenseData.id, {
            food_expenses: food,
            other_expenses: other,
            accommodation_expenses: accommodation,
        }).subscribe();
    }

    downloadXls(): void {
        if (!this.serviceId) return;
        this.expenseService.downloadXls(this.serviceId).subscribe({
            next: (blob: Blob) => {
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `validacion_gastos_${this.serviceId}.xlsx`;
                a.click();
                URL.revokeObjectURL(url);
            }
        });
    }

    openValidationModal(): void {
        this.validationIsValid = true;
        const user = this.utilsService.getDataUser();
        this.validatorName = user?.full_name || user?.name || user?.username || user?.email || '';
        this.inconsistencyDesc = '';
        this.showValidationModal = true;
    }

    closeValidationModal(): void {
        this.showValidationModal = false;
    }

    submitValidation(): void {
        if (!this.expenseData?.id || !this.validatorName.trim()) return;
        this.submittingValidation = true;
        this.expenseService.validateExpense(this.expenseData.id, {
            is_valid:       this.validationIsValid,
            validator_name: this.validatorName.trim(),
            inconsistency:  this.validationIsValid ? null : this.inconsistencyDesc,
        }).subscribe({
            next: (res: any) => {
                this.isValidated = true;
                this.validationInfo = {
                    validated_by:             res.validated_by,
                    validated_at:             res.validated_at,
                    validation_is_valid:      res.validation_is_valid,
                    validation_inconsistency: res.validation_inconsistency,
                };
                this.submittingValidation = false;
                this.closeValidationModal();
            },
            error: () => { this.submittingValidation = false; }
        });
    }

    downloadPdf(): void {
        if (!this.serviceId) return;
        this.expenseService.downloadPdf(this.serviceId).subscribe({
            next: (blob: Blob) => {
                const url = URL.createObjectURL(blob);
                const a = document.createElement('a');
                a.href = url;
                a.download = `validacion_gastos_${this.serviceId}.pdf`;
                a.click();
                URL.revokeObjectURL(url);
            }
        });
    }

    onTagCsvUpload(event: any, trip: 'partida' | 'retorno'): void {
        const file: File = event.target.files[0];
        if (!file || !this.serviceId) return;

        if (trip === 'partida') {
            this.tagTollPartidaUploading = true;
        } else {
            this.tagTollRetornoUploading = true;
        }

        this.expenseService.uploadTagTolls(this.serviceId, file, trip).subscribe({
            next: () => {
                if (trip === 'partida') this.tagTollPartidaUploading = false;
                else this.tagTollRetornoUploading = false;
                this.loadValidation(this.serviceId!);
            },
            error: () => {
                if (trip === 'partida') this.tagTollPartidaUploading = false;
                else this.tagTollRetornoUploading = false;
            }
        });
    }
}
